import * as React from 'react';

import {
  enumUnit,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import { TouchButton } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideLayers } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { InventoryItemsModal } from '../../../_shared/InventoryItemsModal';
import {
  $itemsInterfaceHighlightedMaterialIds,
  $itemsInterfaceHighlightedSectionTitle,
  $itemsInterfaceSuggestedPrices,
  resolveSuggestedPrice,
} from '../_state';
import {
  enumLineItemKind,
  ItemsListEntry,
  NormalizedLineItem,
} from '../_types';
import { EditMixedMaterialDialog } from '../dialogs/EditMixedMaterialDialog';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import {
  $closeMixedChildAdd,
  $mixedChildAddState,
  $mixedDetailsState,
  $setAutoFocusRowId,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';

const ICON_LAYERS = <LucideLayers />;

type AddMixedWidgetProps = {
  onAddItem?: (
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => void;
  onUpdateItem?: (id: string, updates: Partial<NormalizedLineItem>) => void;
  onDeleteItem?: (id: string) => void;
  entries?: ItemsListEntry[];
  facilityDefaultUnit?: Unit;
  modalStyle?: React.CSSProperties;
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl';
};

// ⚛️ ⚛️ ⚛️ ADD MIXED WIDGET ⚛️ ⚛️ ⚛️
export const AddMixedWidget: React.FC<AddMixedWidgetProps> = ({
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  entries = [],
  facilityDefaultUnit = enumUnit.Pounds,
  modalStyle,
  buttonSize,
}) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const highlightedMaterialIds = useAtomValue(
    $itemsInterfaceHighlightedMaterialIds,
  );
  const highlightedSectionTitle = useAtomValue(
    $itemsInterfaceHighlightedSectionTitle,
  );
  const suggestedPrices = useAtomValue($itemsInterfaceSuggestedPrices);
  const mixedChildAddState = useAtomValue($mixedChildAddState);
  const mixedDetailsState = useAtomValue($mixedDetailsState);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const closeMixedChildAdd = useSetAtom($closeMixedChildAdd);
  const inventoryItemsById = useAtomValue($inventoryItemsById);

  // Memoized values
  const mixedParentItem = React.useMemo(
    () =>
      mixedDetailsState.lineItemId
        ? (entries.find(
            (e) =>
              e.kind === enumLineItemKind.LineItem &&
              e.id === mixedDetailsState.lineItemId,
          ) as NormalizedLineItem | undefined)
        : undefined,
    [mixedDetailsState.lineItemId, entries],
  );

  const mixedChildItems = React.useMemo(
    () =>
      mixedDetailsState.lineItemId
        ? (entries.filter(
            (e) =>
              e.kind === enumLineItemKind.LineItem &&
              (e as NormalizedLineItem).parentLineItemId ===
                mixedDetailsState.lineItemId,
          ) as NormalizedLineItem[])
        : [],
    [mixedDetailsState.lineItemId, entries],
  );

  // Callbacks
  const handleAddMixed = React.useCallback(() => {
    if (!onAddItem) return;
    const newItemId = `temp-${uuidv4()}`;
    onAddItem('Mixed', {
      id: newItemId,
      weights: {
        [enumLoadWeightType.Gross]: zeroWeight(enumUnit.Pounds),
        [enumLoadWeightType.Tare]: zeroWeight(enumUnit.Pounds),
        [enumLoadWeightType.Net]: zeroWeight(enumUnit.Pounds),
      },
      pricingUnit: enumUnit.Pounds,
    });
    setAutoFocusRowId(newItemId);
  }, [onAddItem, setAutoFocusRowId]);

  const handleMixedChildMaterialSelect = React.useCallback(
    (materialId?: string) => {
      if (!onAddItem || !mixedChildAddState.parentLineItemId) return;
      const newItemId = `temp-${uuidv4()}`;
      const suggested = resolveSuggestedPrice(suggestedPrices, materialId);
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      onAddItem('Raw', {
        id: newItemId,
        itemId: materialId ?? null,
        parentLineItemId: mixedChildAddState.parentLineItemId,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(unit),
          [enumLoadWeightType.Tare]: zeroWeight(unit),
          [enumLoadWeightType.Net]: zeroWeight(unit),
        },
        unitPrice: suggested?.unitPrice ?? null,
        pricingUnit: unit,
      });
      setAutoFocusRowId(newItemId);
      closeMixedChildAdd();
    },
    [
      onAddItem,
      mixedChildAddState.parentLineItemId,
      setAutoFocusRowId,
      closeMixedChildAdd,
      inventoryItemsById,
    ],
  );

  const handleMixedChildModalOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) closeMixedChildAdd();
    },
    [closeMixedChildAdd],
  );

  return (
    <>
      <TouchButton
        onPress={handleAddMixed}
        className="@AddItemButton w-full"
        isSelected={false}
        leftElement={ICON_LAYERS}
        color="primary"
        variant="outline"
        size={buttonSize}
        aria-label="Add Item"
      >
        {t.scaleTicketPro.actions.addMixed}
      </TouchButton>

      <InventoryItemsModal
        isOpen={mixedChildAddState.isOpen}
        mode="items"
        onOpenChange={handleMixedChildModalOpenChange}
        onSelectMaterial={handleMixedChildMaterialSelect}
        modalStyle={modalStyle}
        highlightedMaterialIds={highlightedMaterialIds}
        highlightedSectionTitle={highlightedSectionTitle}
      />

      {mixedParentItem && onUpdateItem && onAddItem && onDeleteItem && (
        <EditMixedMaterialDialog
          lineItem={mixedParentItem}
          childItems={mixedChildItems}
          onUpdateItem={onUpdateItem}
          onAddItem={onAddItem}
          onDeleteItem={onDeleteItem}
          defaultUnit={facilityDefaultUnit}
        />
      )}
    </>
  );
};
